import { useState, useEffect } from 'react';
import { findCustomerJob } from './server/customerJobs';
import { Search, Package, Calendar, Phone, Mail, Palette, Share2, Check, FileText, Truck, CheckCircle2 } from 'lucide-react';

type Job = Awaited<ReturnType<typeof findCustomerJob>>['jobs'][number];

function getStatusColor(status: string | null): string {
  if (!status) return 'bg-gray-400 text-white';
  const s = status.toLowerCase();
  if (s.includes('complete') || s.includes('done') || s.includes('invoiced')) return 'bg-green-500 text-white';
  if (s.includes('ship') || s.includes('email')) return 'bg-blue-500 text-white';
  if (s.includes('spray') || s.includes('working')) return 'bg-yellow-500 text-black';
  if (s.includes('quality')) return 'bg-orange-500 text-white';
  if (s.includes('hold') || s.includes('stuck') || s.includes('warning')) return 'bg-red-500 text-white';
  if (s.includes('rework') || s.includes('recoat')) return 'bg-pink-500 text-white';
  if (s.includes('archive')) return 'bg-gray-600 text-white';
  if (s.includes('due in')) return 'bg-amber-500 text-black';
  if (s.includes('needs invoiced')) return 'bg-teal-500 text-white';
  if (s.includes('received') || s.includes('filler')) return 'bg-purple-500 text-white';
  if (s.includes('partial')) return 'bg-indigo-500 text-white';
  return 'bg-slate-500 text-white';
}

function getStatusMessage(status: string | null): string {
  if (!status) return '';
  const s = status.toLowerCase();
  if (s.includes('complete')) return 'Your job is finished and ready!';
  if (s.includes('invoiced')) return 'Job complete - invoice sent';
  if (s.includes('ship')) return 'Your parts are ready to ship';
  if (s.includes('email ready')) return 'Update email will be sent soon';
  if (s.includes('spray')) return 'Currently being powder coated';
  if (s.includes('working')) return 'In progress';
  if (s.includes('quality')) return 'Undergoing quality inspection';
  if (s.includes('hold')) return 'On hold - please contact us';
  if (s.includes('received')) return 'Received and in queue';
  return '';
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export default function App() {
  const [customerName, setCustomerName] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedJobId, setCopiedJobId] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Powder Addiction - Job Status Portal';
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName.trim()) return;
    setSearching(true);
    setError(null);
    setSearched(false);
    try {
      const result = await findCustomerJob({ data: { customerName: customerName.trim() } });
      setJobs(result?.jobs ?? []);
      setSearched(true);
    } catch (_err) {
      setError('Unable to find jobs. Please check your company name and try again.');
      setJobs([]);
      setSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const handleShareJob = async (job: Job) => {
    const shareText = `Powder Addiction Job Update\n\nJob: ${job.name}\nStatus: ${job.status || 'In Progress'}\nCompany: ${job.qbCustomerName || customerName}\n\nTrack your jobs: ${window.location.origin}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Powder Addiction - ${job.name}`, text: shareText, url: window.location.href });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopiedJobId(job.id);
      setTimeout(() => setCopiedJobId(null), 2000);
    } catch { /* clipboard failed */ }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Powder Addiction</h1>
              <p className="mt-1 text-sm text-muted-foreground">Job Status Portal</p>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <a href="https://www.powder-addiction.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Search className="h-4 w-4" />www.powder-addiction.com
              </a>
              <a href="tel:352-609-5209" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Phone className="h-4 w-4" />352-609-5209
              </a>
              <a href="mailto:accountspayable@powder-addiction.com" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Mail className="h-4 w-4" />accountspayable@powder-addiction.com
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {/* Search */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-lg sm:p-8">
          <h2 className="mb-2 text-2xl font-semibold text-foreground">Track Your Job Status</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            This portal is <span className="font-medium text-foreground">view only</span> — search your company name to see where your jobs are. Nothing on the job cards can be clicked or changed. Questions? Call or email us.
          </p>
          <div className="mb-4 rounded-lg border-2 border-destructive/60 bg-destructive/10 p-4">
            <p className="text-sm font-bold uppercase leading-relaxed text-foreground">
              Please bring dunnage, shipping blankets, etc. for your pick up. We provide foam to protect product, but it is the driver&apos;s responsibility to stage the trailer for any product that isn&apos;t palletized. We have 4x4, 2x4, 2x2, and 1x2 dunnage and shipping blankets that can be purchased. Please bring cash for shipping materials.
            </p>
          </div>
          <div className="mb-6 rounded-lg border border-border bg-muted/50 p-4 text-sm">
            <p className="mb-2 font-medium text-foreground">How to read your job card</p>
            <ul className="space-y-1.5 text-muted-foreground">
              <li><span className="font-medium text-foreground">Status tag</span> — the colored label shows your job&apos;s current stage (Received, Working On It, Quality Check, Ready to Ship, etc.).</li>
              <li><span className="font-medium text-foreground">Rack Progress</span> — boxes at the bottom show how many racks have finished each step: Prep → Wash → Spray → QC. Example: 3/8 means 3 of 8 racks are done.</li>
              <li><span className="font-medium text-foreground">Timeline &amp; Invoice</span> — received, due, shipped dates, and invoice number once billed.</li>
            </ul>
          </div>
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="customerName" className="text-base font-medium text-foreground">Company Name</label>
              <input
                id="customerName"
                type="text"
                placeholder="Enter your company name (e.g., Acme Fabrication)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                disabled={searching}
                required
              />
              <p className="text-sm text-muted-foreground">
                Enter the company name we have on file for your account.{' '}
                <a href="https://www.powder-addiction.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Visit our website</a>
              </p>
            </div>
            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
            )}
            <button type="submit" className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50" disabled={searching}>
              {searching ? (
                <><div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />Searching...</>
              ) : (
                <><Search className="h-5 w-5" />Find My Jobs</>
              )}
            </button>
          </form>
        </div>

        {/* No Results */}
        {searched && jobs.length === 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-12 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Search className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">No Jobs Found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              We couldn&apos;t find any jobs under &quot;{customerName}&quot;. Please check the spelling and try again.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 text-sm">
              <a href="https://www.powder-addiction.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline"><Search className="h-4 w-4" />Visit our website</a>
              <a href="tel:352-609-5209" className="flex items-center gap-2 text-primary hover:underline"><Phone className="h-4 w-4" />Call us: 352-609-5209</a>
              <a href="mailto:accountspayable@powder-addiction.com" className="flex items-center gap-2 text-primary hover:underline"><Mail className="h-4 w-4" />Email us for help</a>
            </div>
          </div>
        )}

        {/* Job Results */}
        {searched && jobs.length > 0 && (
          <div className="mt-6 space-y-6">
            <h2 className="text-2xl font-semibold text-foreground">
              {jobs.length === 1 ? 'Your Job' : `${jobs.length} Jobs Found`}
            </h2>

            {jobs.map((job) => (
              <div key={job.id} className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                {/* Photos */}
                {job.poUpload.length > 0 && (
                  <div className="grid gap-2 border-b border-border p-4 sm:grid-cols-2 lg:grid-cols-3">
                    {job.poUpload.map((file) => (
                      <div key={file.assetId ?? file.url} className="overflow-hidden rounded-lg bg-muted">
                        <img src={file.url} alt={file.name || 'Job photo'} className="h-48 w-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  {/* Job Header */}
                  <div className="mb-6">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-wrap items-start gap-3">
                        <h3 className="text-2xl font-semibold text-foreground">{job.name}</h3>
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(job.status)}`}>
                          {job.status ?? 'No Status'}
                        </span>
                      </div>
                      <button onClick={() => handleShareJob(job)} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                        {copiedJobId === job.id ? <><Check className="h-4 w-4" />Copied!</> : <><Share2 className="h-4 w-4" />Share</>}
                      </button>
                    </div>
                    <p className="text-base text-muted-foreground">{getStatusMessage(job.status)}</p>
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    {job.qbCustomerName && (
                      <DetailRow icon={<Search className="h-4 w-4 text-primary" />} label="Company" value={job.qbCustomerName} />
                    )}
                    {job.lotpo && (
                      <DetailRow icon={<Package className="h-4 w-4 text-primary" />} label="Work Order / PO Number" value={job.lotpo} />
                    )}
                    {job.colorName && (
                      <DetailRow icon={<Palette className="h-4 w-4 text-primary" />} label="Powder Color" value={job.colorName} />
                    )}
                    {job.jobNotes && (
                      <DetailRow icon={<Search className="h-4 w-4 text-primary" />} label="Notes" value={job.jobNotes} />
                    )}

                    {/* Timeline */}
                    {(job.received || job.due || job.dateShipped) && (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <Calendar className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Timeline</p>
                          <div className="mt-1 space-y-1 text-base text-foreground">
                            {job.received && <p><span className="text-muted-foreground">Received:</span> {fmtDate(job.received)}</p>}
                            {job.due && <p><span className="text-muted-foreground">Due Date:</span> {fmtDate(job.due)}</p>}
                            {job.dateShipped && (
                              <p className="flex items-center gap-1.5">
                                <Truck className="h-4 w-4 text-green-600" />
                                <span className="text-muted-foreground">Shipped:</span>
                                <span className="font-medium text-green-600">{fmtDate(job.dateShipped)}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Invoice */}
                    {(job.qbInvoice || job.invoiceDate) && (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Invoice</p>
                          <div className="mt-1 space-y-1 text-base text-foreground">
                            {job.qbInvoice && <p><span className="text-muted-foreground">Invoice #:</span> <span className="font-medium">{job.qbInvoice}</span></p>}
                            {job.invoiceDate && <p><span className="text-muted-foreground">Invoice Date:</span> {fmtDate(job.invoiceDate)}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Rack Progress */}
                    {job.rackProgress.total > 0 && (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-muted-foreground">Rack Progress</p>
                          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                            <div className="rounded-md bg-purple-50 px-3 py-2 text-center">
                              <p className="text-xs font-medium text-purple-600">Prep</p>
                              <p className="text-lg font-bold text-purple-700">{job.rackProgress.prep}/{job.rackProgress.total}</p>
                            </div>
                            <div className="rounded-md bg-blue-50 px-3 py-2 text-center">
                              <p className="text-xs font-medium text-blue-600">Wash</p>
                              <p className="text-lg font-bold text-blue-700">{job.rackProgress.wash}/{job.rackProgress.total}</p>
                            </div>
                            <div className="rounded-md bg-green-50 px-3 py-2 text-center">
                              <p className="text-xs font-medium text-green-600">Spray</p>
                              <p className="text-lg font-bold text-green-700">{job.rackProgress.spray}/{job.rackProgress.total}</p>
                            </div>
                            <div className="rounded-md bg-orange-50 px-3 py-2 text-center">
                              <p className="text-xs font-medium text-orange-600">QC</p>
                              <p className="text-lg font-bold text-orange-700">{job.rackProgress.qc}/{job.rackProgress.total}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">{icon}</div>
      <div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="text-base font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}
